import { create } from 'zustand'
import type { Contact } from '../types/contact.types'
import { DEFAULT_VALUE } from '../types/enum.types'

export interface PendingChangesPayload {
  recordId: string
  contacts: Contact[]
  maxContacts: number
}

export interface ContactStoreState {
  recordId: string
  contacts: Contact[]
  pendingAdds: Contact[]
  pendingEdits: Map<string, Contact>
  pendingDeletes: Set<string>
  maxContacts: number
}

export interface ContactStoreActions {
  loadContacts: (recordId: string, contacts: Contact[]) => void
  addContact: (contact: Omit<Contact, 'id'>) => void
  editContact: (id: string, data: Partial<Contact>) => void
  deleteContact: (id: string) => void
  restoreContact: (id: string) => void
  getPendingChanges: () => PendingChangesPayload | null
  resetChanges: () => void
  reset: () => void
  setMaxContacts: (max: number) => void
}

const initialState: ContactStoreState = {
  recordId: '',
  contacts: [],
  pendingAdds: [],
  pendingEdits: new Map(),
  pendingDeletes: new Set(),
  maxContacts: 10,
}

export const useContactStore = create<ContactStoreState & ContactStoreActions>()(
  (set, get) => ({
    ...initialState,

    loadContacts: (recordId, contacts) => {
      set({
        recordId,
        contacts,
        pendingAdds: [],
        pendingEdits: new Map(),
        pendingDeletes: new Set(),
      })
    },

    addContact: (contactData) => {
      const contact: Contact = {
        ...contactData,
        id: crypto.randomUUID(),
      }
      set((state) => ({
        contacts: [...state.contacts, contact],
        pendingAdds: [...state.pendingAdds, contact],
      }))
    },

    editContact: (id, data) => {
      set((state) => {
        const updatedContacts = state.contacts.map((c) =>
          c.id === id ? { ...c, ...data } : c,
        )
        const updatedContact = updatedContacts.find((c) => c.id === id)
        if (!updatedContact) return state

        const newPendingEdits = new Map(state.pendingEdits)
        newPendingEdits.set(id, updatedContact)

        return {
          contacts: updatedContacts,
          pendingEdits: newPendingEdits,
        }
      })
    },

    deleteContact: (id) => {
      set((state) => {
        const newPendingDeletes = new Set(state.pendingDeletes)
        newPendingDeletes.add(id)

        // AC4: Auto-reassign default when deleting the default contact
        const deletedContact = state.contacts.find((c) => c.id === id)
        if (deletedContact?.useFor.includes(DEFAULT_VALUE)) {
          const firstRemaining = state.contacts.find(
            (c) => c.id !== id && !newPendingDeletes.has(c.id ?? ''),
          )
          if (firstRemaining?.id) {
            const newPendingEdits = new Map(state.pendingEdits)
            const updated = {
              ...firstRemaining,
              useFor: firstRemaining.useFor.includes(DEFAULT_VALUE)
                ? firstRemaining.useFor
                : [DEFAULT_VALUE, ...firstRemaining.useFor],
              isDefault: true,
            }
            newPendingEdits.set(firstRemaining.id, updated)
            return {
              contacts: state.contacts.map((c) =>
                c.id === firstRemaining.id ? updated : c,
              ),
              pendingDeletes: newPendingDeletes,
              pendingEdits: newPendingEdits,
            }
          }
        }

        return { pendingDeletes: newPendingDeletes }
      })
    },

    restoreContact: (id) => {
      set((state) => {
        const newPendingDeletes = new Set(state.pendingDeletes)
        newPendingDeletes.delete(id)

        const restoredContact = state.contacts.find((c) => c.id === id)
        if (restoredContact?.useFor.includes(DEFAULT_VALUE)) {
          // Reverse the auto-reassignment: remove default from whoever currently has it
          const currentDefault = state.contacts.find(
            (c) =>
              c.id !== id &&
              !newPendingDeletes.has(c.id ?? '') &&
              c.useFor.includes(DEFAULT_VALUE),
          )
          if (currentDefault?.id) {
            const newPendingEdits = new Map(state.pendingEdits)
            const updated = {
              ...currentDefault,
              useFor: currentDefault.useFor.filter((u) => u !== DEFAULT_VALUE),
              isDefault: false,
            }
            newPendingEdits.set(currentDefault.id, updated)
            return {
              contacts: state.contacts.map((c) =>
                c.id === currentDefault.id ? updated : c,
              ),
              pendingDeletes: newPendingDeletes,
              pendingEdits: newPendingEdits,
            }
          }
        }

        return { pendingDeletes: newPendingDeletes }
      })
    },

    getPendingChanges: () => {
      const state = get()
      const hasPendingAdds = state.pendingAdds.length > 0
      const hasPendingEdits = state.pendingEdits.size > 0
      const hasPendingDeletes = state.pendingDeletes.size > 0

      if (!hasPendingAdds && !hasPendingEdits && !hasPendingDeletes) {
        return null
      }

      const pendingAddIds = new Set(state.pendingAdds.map((c) => c.id))

      const contacts = state.contacts
        .filter((c) => c.id == null || !state.pendingDeletes.has(c.id))
        .map((c) => {
          if (pendingAddIds.has(c.id)) {
            return { ...c, id: null }
          }
          return c
        })

      return {
        recordId: state.recordId,
        contacts,
        maxContacts: state.maxContacts,
      }
    },

    resetChanges: () => {
      set({
        pendingAdds: [],
        pendingEdits: new Map(),
        pendingDeletes: new Set(),
      })
    },

    // Unlike resetChanges (which only clears the pending-change trackers, leaving `contacts` as
    // the just-persisted truth — correct right after an EDIT save), this also blanks `contacts`
    // and `recordId` back to their initial values. Call this after a CREATE succeeds: the 'new'
    // bucket's content now belongs to a real record elsewhere, so leaving it here would leak into
    // the next unrelated "start creating a new record" session under that same 'new' recordId —
    // ContactManager's own mount effect only re-fetches when `recordId` prop actually CHANGES
    // value, which never happens between two separate creation sessions (both are literally
    // 'new'), so a stale `contacts` array is otherwise never cleared on its own.
    reset: () => {
      set({
        recordId: '',
        contacts: [],
        pendingAdds: [],
        pendingEdits: new Map(),
        pendingDeletes: new Set(),
      })
    },

    setMaxContacts: (max) => {
      set({ maxContacts: max })
    },
  }),
)
